import { useCallback, useMemo, useRef } from 'react';

import { getValues } from '../utils/record';

type MapKey = string | number;

type MultiRefMap<TKey extends MapKey, TValue> = {
  callbacks: Map<TKey, (value: TValue | null) => void>;
  values: Map<TKey, TValue>;
};

const useMapRef = <TKey extends MapKey, TValue>() => {
  const ref = useRef<MultiRefMap<TKey, TValue>>();

  const getMaps = useCallback(() => {
    if (!ref.current)
      ref.current = {
        callbacks: new Map(),
        values: new Map(),
      };

    return ref.current;
  }, []);

  const getValuesMap = useCallback(() => getMaps().values, [getMaps]);

  const getValueByIndex = useCallback(
    (index: number) => {
      const map = getValuesMap();

      // not super efficient, so a loop might work better for larger data sets
      return getValues(map)[index] ?? null;
    },
    [getValuesMap],
  );

  const getFirstValue = useCallback(() => getValueByIndex(0), [getValueByIndex]);

  const getLastValue = useCallback(() => {
    const length = getValuesMap().size;

    return getValueByIndex(length - 1);
  }, [getValueByIndex, getValuesMap]);

  const appendRefItem = useCallback(
    (key: TKey, value: TValue) => {
      const map = getValuesMap();

      map.set(key, value);
    },
    [getValuesMap],
  );

  const cleanupRefItem = useCallback(
    (key: TKey) => {
      // keeping callbacks alive across child (un)mounts (maximum memo() effectiveness in my use case)
      // => will only be killed if the above ref is unmounted alongside the entire parent
      // this is a trade off however and could lead to a memory leak in extreme cases of fe infinite scrolling
      const map = getValuesMap();

      map.delete(key);
    },
    [getValuesMap],
  );

  const createEntryCallback = useCallback(
    (key: TKey) => {
      const map = getMaps().callbacks;

      const cachedCallback = map.get(key);

      if (cachedCallback) return cachedCallback;

      const newCallback = (value: TValue | null) => {
        if (value === null) cleanupRefItem(key);
        else appendRefItem(key, value);
      };

      map.set(key, newCallback);

      return newCallback;
    },
    [appendRefItem, cleanupRefItem, getMaps],
  );

  return useMemo(
    () => ({
      createCallback: createEntryCallback,
      current: getValuesMap,
      getFirst: getFirstValue,
      getLast: getLastValue,
      getValueByIndex,
      ref,
    }),
    [createEntryCallback, getFirstValue, getLastValue, getValueByIndex, getValuesMap],
  );
};

export default useMapRef;
